// src/components/Replan/TextEditor.js
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

const TextEditor = ({ textNode, stageRef, onChange, onClose, initialText }) => {
  const textareaRef = useRef(null);
  const boundsRef = useRef({ maxHeight: 400 });
  const positionRef = useRef({ top: 0, left: 0 });

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textNode || !stageRef.current || !textarea) {
      return;
    }

    const stage = stageRef.current;

    // Get text position
    const textPosition = textNode.absolutePosition();
    const stageBox = stage.container().getBoundingClientRect();

    const minWidth = 160;
    const minHeight = 60;
    const padding = 8;

    const desiredWidth = Math.max(textNode.width(), 400);
    let left = stageBox.left + textPosition.x;
    let top = stageBox.top + textPosition.y;

    const maxLeft = stageBox.right - minWidth - padding;
    const maxTop = stageBox.bottom - minHeight - padding;
    left = Math.min(Math.max(left, stageBox.left + padding), maxLeft);
    top = Math.min(Math.max(top, stageBox.top + padding), maxTop);

    const maxWidth = Math.max(minWidth, stageBox.right - left - padding);
    const maxHeight = Math.max(minHeight, stageBox.bottom - top - padding);

    // Position the textarea within the stage container bounds
    textarea.style.top = `${top}px`;
    textarea.style.left = `${left}px`;
    textarea.style.width = `${Math.min(desiredWidth, maxWidth)}px`;
    textarea.style.maxHeight = `${maxHeight}px`;

    boundsRef.current = { maxHeight };
    positionRef.current = { top, left };

    // Auto-resize to fit content initially
    textarea.style.height = 'auto';
    textarea.style.height = Math.max(textarea.scrollHeight, minHeight) + 'px';

    // Focus and select
    textarea.focus();
    textarea.select();
  }, [textNode, stageRef]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
    // Allow regular Enter for new lines
  };

  const handleInput = (e) => {
    // Auto-resize textarea to fit content
    const textarea = e.target;
    textarea.style.height = 'auto';
    const maxHeight = boundsRef.current.maxHeight || 400;
    const nextHeight = Math.max(textarea.scrollHeight, 60);
    textarea.style.height = `${Math.min(nextHeight, maxHeight)}px`;
  };

  const handleBlur = () => {
    onChange(textareaRef.current.value);
    onClose();
  };

  if (!textNode) return null;

  // Render the textarea outside the Konva tree using a portal
  return ReactDOM.createPortal(
    <textarea
      ref={textareaRef}
      defaultValue={initialText || textNode.text()}
      onKeyDown={handleKeyDown}
      onInput={handleInput}
      onBlur={handleBlur}
      title="Enter for new line • Ctrl+Enter to save • Esc to cancel"
      style={{
        position: 'absolute',
        // fontSize: `${textNode.fontSize()}px`,
        fontFamily: textNode.fontFamily(),
        border: '2px solid #3B82F6',
        borderRadius: '4px',
        padding: '8px',
        background: 'white',
        outline: 'none',
        resize: 'vertical',
        zIndex: 1000,
        margin: 0,
        overflow: 'auto',
        minHeight: '60px',
        maxHeight: '400px',
      }}
      placeholder="Title\n---\nBody text here..."
    />,
    document.body
  );
};

export default TextEditor;
